import { AdSettings } from './ads';
import { marketCodes } from '../markets';

interface ClassicProxy {
  host: string;
  port: number;
  secure?: boolean;
}

export interface Site {
  production?: string;
  classic?: string;
  analytics?: string;
  brands?: string;
  forum?: string;
  mobile?: string;
}

interface Api {
  default: string;
}

type CampaignDateMap = {
  [key: string]: string;
};

export interface Campaigns {
  base?: string;
  previousCampaignDates?: CampaignDateMap;
}

interface Legacy {
  brand?: string;
  category?: string;
  fight?: string;
  forum?: string;
  info?: string;
  lists?: string;
  minhembio?: string;
  plugin?: string;
  product?: string;
  raw?: string;
  // eslint-disable-next-line camelcase
  store_info?: string;
  // eslint-disable-next-line camelcase
  user_login?: string;
  // eslint-disable-next-line camelcase
  mobile_site?: string;
}

export interface Url {
  campaigns?: string;
  legacy?: Legacy;
}

type SocialNetwork = {
  link?: string;
};

type Facebook = SocialNetwork & {
  enableLogin?: boolean;
  clientID?: string;
  clientSecret?: string;
  callbackURL?: string;
};

type Twitter = SocialNetwork & {
  enableLogin?: boolean;
  callbackURL?: string;
  consumerKey: string;
  consumerSecret: string;
};

type Schibsted = SocialNetwork & {
  enableLogin: boolean;
  callbackURL?: string;
  clientID?: string;
  clientSecret?: string;
  host?: string;
};

type SocialNetworks = {
  facebook?: Facebook;
  instagram?: SocialNetwork;
  twitter?: Twitter;
  schibsted?: Schibsted;
};

interface SchibstedOtherSitesLinks {
  name: string;
  siteUrl: string;
}

interface Email {
  noreply?: string;
}

export interface Cdn {
  base?: string;
  origin?: string;
  images?: string;
  offerImages?: string;
}

interface Path {
  path?: string;
  relativePath?: string;
}

interface Routes {
  themes?: Path;
  campaign?: Path;
  productAlert?: Path;
  productCompare?: Path;
  shopOfTheYear?: Path;
  shopOfTheYearCategory?: Path;
  shopOfTheYearWinners?: Path;
  editorialContentListing?: Path;
  categoryIndex?: Path;
  expertProductTest?: Path;
  expertToplists?: Path;
  expertGuide?: Path;
  expertProfile?: Path;
  expertLandingPage?: Path;
  expertCategory?: Path;
  changelog?: Path;
}

interface PopularCampaignFilterPath {
  title: string;
  path: string;
  description?: string;
}

interface InfoPageMap {
  userAgreement?: number;
  priceInformation?: number;
  privacyPolicy?: number;
  cookiePolicy?: number;
  contact?: number;
  about?: number;
  registerShop?: number;
  media?: number;
  disclaimer?: number;
  aboutReviews?: number;
  adlink?: string;
  themes?: string;
  careers?: string;
  articles?: string;
  shopOfTheYear?: string;
  categoryIndex?: string;
  privacySettings?: string;
  expertLogin?: string;
  experts?: string;
  supportPage?: string;
  newsroom?: string;
  blackFriday?: string;
}

interface Newsletter {
  apiUrl?: string;
  apiKey?: string;
  list?: string;
  gansubFormId?: string;
}

export interface Apps {
  ios?: string;
  android?: string;
}

export interface SourcePointCMP {
  enabled?: boolean;
  privacyManagerID?: number;
  priceSpyVendorID?: string;
}

interface Responsive {
  cookieVersion?: string;
  cookieUrl?: string;
  privacyUrl?: string;
  privacySettingsUrl?: string;
}

interface PrivacySettings {
  responsive?: Responsive;
  desktop?: Responsive;
  mobile?: Responsive;
  native?: Responsive;
}

export interface Ads extends AdSettings {
  active: boolean;
}
interface ModuleSettings {
  enabled: boolean;
  datePlaceholderFormat?: string;
}

interface Modules {
  priceInfo?: ModuleSettings;
  categoryInfo?: ModuleSettings;
  userReviewPurchaseDate?: ModuleSettings;
  readableProductInfo?: ModuleSettings;
}

export enum PrivacySettingToggles {
  BehaviourTrackingImprovement = 'behavior_tracking_improvement',
  BehaviorTrackingMarketing = 'behavior_tracking_marketing',
  BehaviorTrackingSurveys = 'behavior_tracking_surveys',
  PersonalizedAds = 'personalized_ads',
  Personalization = 'personalization',
}

export type PrivacySettingToggle = { [key in PrivacySettingToggles]: boolean };

interface trackingToggle {
  name?:
    | PrivacySettingToggles.BehaviourTrackingImprovement
    | PrivacySettingToggles.BehaviorTrackingMarketing
    | PrivacySettingToggles.BehaviorTrackingSurveys
    | PrivacySettingToggles.PersonalizedAds
    | PrivacySettingToggles.Personalization;
  default: boolean;
}

interface OfferList {
  filters?: {
    hiddenFilters?: FilterType[];
    defaultActiveFilters?: FilterType[];
  };
}

export enum PaymentProviderGroup {
  Direct,
  Invoice,
  Card,
}

interface PaymentProviders {
  priorities?: string[];
  groupPriorities?: PaymentProviderGroup[];
}
interface CdnCache {
  enabled: boolean;
  mode: 'internal' | 'external';
}

type Articles = {
  aboutExperts?: {
    path: string;
  };
  recruiting?: {
    path: string;
  };
};

export enum FilterTypeEnum {
  PRICES_INC_SHIPPING = 'prices_inc_shipping',
  IN_STOCK = 'in_stock',
  FEATURED_SHOP = 'featured_shop',
  AUTHORIZED_DEALER = 'authorized_dealer',
  PICKUP_ONLY = 'pickup_only',
  INTERNATIONAL = 'international',
  NOT_FOR_SALE = 'not_for_sale',
  VARIANT_SIZE = 'variant_size',
  CONDITION = 'condition',
}

export type FilterType =
  | FilterTypeEnum.PRICES_INC_SHIPPING
  | FilterTypeEnum.IN_STOCK
  | FilterTypeEnum.FEATURED_SHOP
  | FilterTypeEnum.AUTHORIZED_DEALER
  | FilterTypeEnum.PICKUP_ONLY
  | FilterTypeEnum.INTERNATIONAL
  | FilterTypeEnum.NOT_FOR_SALE
  | FilterTypeEnum.VARIANT_SIZE
  | FilterTypeEnum.CONDITION;

export type Experts = {
  enabled: boolean;
  apiUrl?: string;
  graphQLUrl?: string;
  clickTrackingEnabled?: boolean;
};

interface ProductVariants {
  shoeSizeStandard: string;
}

export interface Market {
  countryCode: MarketCodes;
  active: boolean;
  sitename?: string;
  permanentRedirect?: boolean;
  redirectHosts?: string[];
  superSearchEndpoint?: string;
  trackerProxy?: string;
  useClassicUrls?: boolean;
  classicProxy?: ClassicProxy;
  hrefLang?: string[];
  locale: string;
  localeStandard: string;
  sites: Site;
  api?: Api;
  campaigns: Campaigns;
  googleTagManager?: string;
  translation: string;
  timezone?: string;
  currencyCode?: string;
  url: Url;
  socialNetworks: SocialNetworks;
  schibstedOtherSitesLinks?: SchibstedOtherSitesLinks[];
  googleSiteVerification?: string;
  campaignFeaturedList?: Array<{ campaignId: number; listId: number }>;
  email?: Email;
  cdn: Cdn;
  routes?: Routes;
  popularCampaignFilterPaths?: PopularCampaignFilterPath[];
  infoPages?: number[];
  infoPageMap?: InfoPageMap;
  featuredArticle?: number;
  newsletter?: Newsletter;
  sourcePointCMP?: SourcePointCMP;
  apps?: Apps;
  ads: Ads;
  privacySettings?: PrivacySettings;
  modules?: Modules;
  showSchibstedCoBranding?: boolean;
  showClassicSwitch: boolean;
  showBrandingTagline?: boolean;
  trackingToggles?: trackingToggle[];
  shopOfTheYear: boolean;
  showProductInformationTexts?: boolean;
  offerList?: OfferList;
  paymentProviders?: PaymentProviders;
  structuredData?: any;
  cdnCache?: CdnCache;
  useHashForProductTabs?: boolean;
  lazyLoadIntercom?: boolean;
  productVariants?: ProductVariants;
  articles?: Articles;
  experts: Experts;
  keywordLandingPagesEnabled?: boolean;
  featuredCollectionsEnabled?: boolean;
  robotsTxt?: string[];
  fallbackRedirects?: Record<string, string>;
}

export type MarketCodes = typeof marketCodes[number];

export type Markets = {
  [key in MarketCodes]?: Market;
};
